import React, { useRef } from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import { useSettingsObject } from '../../../settings/hooks';
import { getUserThemeColor, shadeColor, toRgba } from '../../../../ui/theme/colors';
import GoalCard from './GoalCard';
import ActionSteps from './ActionSteps';
import InspirationSection from './InspirationSection';
import CoursioNetworkCTA from './CoursioNetworkCTA';
import PersonalizedHeader from './PersonalizedHeader';
import VisionGoalCard from './VisionGoalCard';
import MOCK_GOALS_DATA from './mockGoalsData';
import PageSection from '../../../../ui/Page/PageSection';

const Container = styled(Flex)`
  width: 100%;
  max-width: 100%;
  padding: 0;
`;

const MainLayout = styled(Flex)`
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const MainColumn = styled(Flex)`
  flex: 1;
  min-width: 0;
`;

const Sidebar = styled(Flex)`
  width: 320px;
  position: sticky;
  top: 24px;

  @media (max-width: 1200px) {
    width: 100%;
    position: static;
  }
`;

const GoalRowWrapper = styled.div`
  margin-bottom: 20px;
  scroll-margin-top: 100px; /* Offset for fixed headers if any */
`;

const GoalRow = styled(Flex)`
  background: ${(props) => {
    if (props.$hasBackground && props.$lightBrandColor) {
      return props.$lightBrandColor;
    }
    return theme.colors.NEU_0;
  }};
  border-radius: 12px;
  padding: 24px;
  gap: 36px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  transition: all 0.3s ease;
  width: 100%;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${(props) => props.$hoverShadow || `0 4px 16px ${toRgba(theme.colors.NEU_999, 0.08)}`};
  }

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const GoalSummary = styled(Flex)`
  min-width: 380px;
  width: 100%;

  @media (max-width: 900px) {
    min-width: 100%;
  }
`;

const ActionPlanSection = styled(Flex)`
  width: 100%;
  min-width: 0;
`;

const SectionDivider = styled.div`
  width: 100%;
  height: 0;
  margin: 24px 0;
`;

/**
 * OwnerDashboardContent - Owner Goals Dashboard
 *
 * The primary dashboard view for platform owners, featuring:
 *
 * 1. PERSONALIZATION:
 *    - Personalized header with user's name and business name
 *    - Time-based greeting (Good morning/afternoon/evening)
 *    - Brand color integration throughout
 *
 * 2. VISION GOAL:
 *    - Prominent vision goal card at the top
 *    - Frames the 3 tactical goals as supporting objectives
 *    - Shows overall progress and key metrics
 *
 * 3. STRUCTURE:
 *    - PersonalizedHeader (user connection)
 *    - VisionGoalCard (main overarching goal)
 *    - 3 Tactical Goals (supporting the vision)
 *    - Sidebar with inspiration
 */
const OwnerDashboardContent = () => {
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = userTheme?.color ? getUserThemeColor(userTheme?.color) : theme.colors.PRIMARY;
  const lightBrandColor = shadeColor(userTheme?.color, 0.96);

  const goals = [MOCK_GOALS_DATA.salesRevenue, MOCK_GOALS_DATA.courseCreation, MOCK_GOALS_DATA.courseDistribution];

  const visionGoal = MOCK_GOALS_DATA.vision;

  // Create refs for each goal to enable scrolling
  const goalRefs = useRef({});

  const handleGoalClick = (goalId) => {
    const goalElement = goalRefs.current[goalId];
    if (goalElement) {
      goalElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  };

  return (
    <Container direction='column'>
      {/* Personalization: User-specific header */}
      <PageSection>
        <PersonalizedHeader brandColor={brandColor} />

        {/* Vision Goal: Main overarching objective with supporting goals summary */}
        <VisionGoalCard visionGoal={visionGoal} goals={goals} brandColor={brandColor} onGoalClick={handleGoalClick} />

        {/* Goals Overview: Metrics summary */}
        {/* <GoalsOverview goals={goals} brandColor={brandColor} /> */}

        {/* Section divider */}
        <SectionDivider />

        {/* Tactical Goals Section */}
        <Typo.ContentBigHeader $marginBottom='32px'>Your Strategic Goals</Typo.ContentBigHeader>

        <MainLayout>
          {/* Main Column with Goals */}
          <MainColumn direction='column'>
            {goals.map((goal, i) => {
              return (
                <GoalRowWrapper key={goal.id} ref={(el) => (goalRefs.current[goal.id] = el)}>
                  <GoalRow align='flex-start' $hasBackground={i % 2 === 0} $lightBrandColor={lightBrandColor}>
                    {/* Goal Summary */}
                    <GoalSummary direction='column' width='100%'>
                      <GoalCard goal={goal} brandColor={brandColor} />
                    </GoalSummary>

                    {/* Action Plan */}
                    <ActionPlanSection direction='column'>
                      <ActionSteps actionPlan={goal.actionPlan} goalTitle={goal.title} compact={false} />
                    </ActionPlanSection>
                  </GoalRow>
                </GoalRowWrapper>
              );
            })}

            {/* Coursio Network CTA */}
            <CoursioNetworkCTA />
          </MainColumn>

          {/* Sidebar with Tips & Resources */}
          <Sidebar direction='column' gap='24'>
            <InspirationSection inspiration={MOCK_GOALS_DATA.salesRevenue.inspiration} goalTitle='Your Goals' />
          </Sidebar>
        </MainLayout>
      </PageSection>
    </Container>
  );
};

export default OwnerDashboardContent;
